
import PropTypes from "prop-types";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { formatNumber } from "utils/numberUtils";
import Tooltip from "@mui/material/Tooltip";

// -------------------------------------------------------
// Component Definition
// -------------------------------------------------------
function SimpleStatisticsCard({
  color = "info",
  title,
  count,
  fullCount = null,
  percentage = {
    color: "success",
    amount: "",
  },
  icon,
  description = ""
}) {
  const isPositive = parseFloat(percentage.amount) > 0;
  const isNegative = parseFloat(percentage.amount) < 0;

  // 1. Prepare Tooltip Content for Percentage Change
  const percentageTooltipContent = percentage.tooltip ? (
    <div>
      <MDTypography variant="caption" color="white" fontWeight="regular">
        Current Value:{" "}
        <span style={{ fontWeight: 700 }}>
          {percentage.tooltip.currentValue?.toLocaleString()}
        </span>
        <br />
        <span style={{ fontSize: "0.7rem" }}>
          ({percentage.tooltip.currentRange})
        </span>
        <br />
        Previous Value:{" "}
        <span style={{ fontWeight: 700 }}>
          {percentage.tooltip.previousValue?.toLocaleString()}
        </span>
        <br />
        <span style={{ fontSize: "0.7rem" }}>
          ({percentage.tooltip.previousRange})
        </span>
      </MDTypography>
    </div>
  ) : (
    percentage.amount
  );

  // 2. Prepare Tooltip Content for Main Count
  const countTooltipContent = fullCount?.toLocaleString() || count;


  return (
    <Card
      sx={{
        boxShadow: 2,
        transition: "all 0.3s ease-in-out",
        "&:hover": { boxShadow: 6 },
        p: 1.5,
      }}
    >
      <MDBox display="flex" justifyContent="space-between" alignItems="center">
        {/* 🟣 Left section: Icon + % */}
        <MDBox
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="flex-start"
          sx={{
            width: "4.5rem",
            flexShrink: 0,
          }}
        >
          {/* Floating icon */}
          <Tooltip title={description} placement="right" arrow>
            <MDBox
              variant="gradient"
              bgColor={color}
              // color={color === "light" ? "dark" : "white"}
              color="black"
              coloredShadow={color}
              borderRadius="xl"
              display="flex"
              justifyContent="center"
              alignItems="center"
              width="3rem"
              height="3rem"
              mt={-4}
              mb={1.5}
              sx={{
                boxShadow: "0 2px 6px rgba(0,0,0,0.12)",
                cursor: "help", // Indicate interactivity
              }}
            >
              <Icon fontSize="medium" color="inherit">
                {icon}
              </Icon>
            </MDBox>
          </Tooltip>

          {/* Percentage below icon */}
          {/* 🔴 Tooltip added to the percentage display */}
          <Tooltip title={percentageTooltipContent} arrow placement="bottom">
            <MDTypography
              variant="button"
              fontWeight="bold"
              color={percentage.color}
              display="flex"
              alignItems="center"
              justifyContent="center"
              sx={{
                minHeight: "1.5rem",
                whiteSpace: "nowrap",
                cursor: "default",
              }}
            >
              {percentage.amount}&nbsp;
              {isPositive ? (
                <ArrowUpwardIcon sx={{ fontSize: 16, color: "green" }} />
              ) : isNegative ? (
                <ArrowDownwardIcon sx={{ fontSize: 16, color: "red" }} />
              ) : null}
            </MDTypography>
          </Tooltip>
        </MDBox>

        {/* 🟢 Right section: title + count */}
        <MDBox
          textAlign="right"
          lineHeight={1.2}
          sx={{
            flexGrow: 1,
            minWidth: 0,
          }}
        >
          <MDTypography variant="button" fontWeight="light" color="text" mb={0.5}>
            {title}
          </MDTypography>
          {/* 🟣 Tooltip added to the count display */}
          <Tooltip title={countTooltipContent} arrow placement="bottom-end">
            <MDTypography variant="h4" fontWeight="medium" sx={{ cursor: "default" }}>
              {formatNumber(count || 0)}
            </MDTypography>
          </Tooltip>
        </MDBox>
      </MDBox>
    </Card>
  );
}

// -------------------------------------------------------
// Prop Types
// -------------------------------------------------------

SimpleStatisticsCard.propTypes = {
  color: PropTypes.oneOf([
    "primary",
    "secondary",
    "info",
    "accent",
    "success",
    "warning",
    "error",
    "light",
    "dark",
  ]),
  title: PropTypes.string.isRequired,
  // Count can be the formatted string or the raw number
  count: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  // The raw, unformatted number for the main count tooltip
  fullCount: PropTypes.number,
  percentage: PropTypes.shape({
    color: PropTypes.oneOf([
      "primary",
      "secondary",
      "info",
      "accent",
      "success",
      "warning",
      "error",
      "dark",
      "white",
    ]),
    amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    // New optional prop shape for detailed tooltip
    tooltip: PropTypes.shape({
      currentValue: PropTypes.number,
      previousValue: PropTypes.number,
      currentRange: PropTypes.string,
      previousRange: PropTypes.string,
    }),
  }),
  icon: PropTypes.node.isRequired,
  description: PropTypes.oneOfType([PropTypes.string, PropTypes.node]), // Tooltip description
};

export default SimpleStatisticsCard;